/**
 * Host page - create a new game session with device detection.
 * Automatically suggests host mode based on screen size.
 */

import { useState, FormEvent, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  createSession,
  joinSession,
  updateHostMode,
  getGameModes,
  notifyDevicePairingSession,
  ApiError,
} from '../services/api'
import { hasStoredPairing, takeStoredPairing } from '@/services/pairingSocket'
import { isTvScreen } from '@/lib/device'
import { usePlayerStore } from '../stores/playerStore'
import { useAuthStore, selectIsAuthenticated } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PixelIcon } from '@/components/low/PixelIcon'
import { BrandLogo } from '@/components/low/BrandLogo'

type HostMode = 'player' | 'display_only'

export function HostPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { gameMode } = useParams<{ gameMode: string }>()

  // Form state
  const [hostName, setHostName] = useState('')
  const [hostMode, setHostMode] = useState<HostMode>('player')
  const [suggestedMode, setSuggestedMode] = useState<HostMode>('player')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [gameModeFreeCutoff, setGameModeFreeCutoff] = useState<string | null | undefined>(undefined)

  // Store
  const { setPlayer, playerId, sessionCode, isHost, clear: clearPlayer } = usePlayerStore()
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const user = useAuthStore((s) => s.user)

  // Track if user has an existing active session
  const hasExistingSession = !!(playerId && sessionCode && isHost)

  // Fetch game mode info to check free_tier_cutoff
  useEffect(() => {
    if (!gameMode) return
    getGameModes()
      .then((data) => {
        const modeInfo = data.modes.find((m) => m.id === gameMode)
        if (modeInfo === undefined) {
          setGameModeFreeCutoff(null)
        } else {
          setGameModeFreeCutoff(modeInfo.free_tier_cutoff)
        }
      })
      .catch(() => setGameModeFreeCutoff(null))
  }, [gameMode])

  // Show demo banner: authenticated host with free plan + mode has cutoff
  const showDemoBanner =
    isAuthenticated &&
    user?.plan_tier === 'free' &&
    gameModeFreeCutoff != null

  // Device detection on mount
  useEffect(() => {
    const detectDevice = () => {
      const mode: HostMode = isTvScreen() ? 'display_only' : 'player'
      setSuggestedMode(mode)
      setHostMode(mode)
    }

    detectDevice()
    window.addEventListener('resize', detectDevice)
    return () => window.removeEventListener('resize', detectDevice)
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    await createAndEnterLobby(hostMode)
  }

  const createAndEnterLobby = async (mode: HostMode) => {
    setIsLoading(true)
    setError(null)

    // Clear any existing session before creating new one
    if (hasExistingSession) {
      clearPlayer()
    }

    // TV logged in by QR: send the approving phone to join this session. Called only
    // after the host device joined, so the phone cannot become Player.is_host.
    // Lobby navigation does not wait for it.
    const reportSessionToPairedPhone = (sessionCode: string) => {
      const pairingCode = takeStoredPairing()
      if (pairingCode === null) return
      notifyDevicePairingSession(pairingCode, sessionCode).catch((err: ApiError) => {
        toast.error(err.message ?? t('host.pairingNotifyError'))
      })
    }

    try {
      // Create session
      const sessionResponse = await createSession({
        game_mode: gameMode as 'bingo' | 'mafia',
      })

      // Use default host name if not provided
      const finalHostName = hostName.trim() || 'Host'

      // If display_only → update host_mode in backend and join as TV
      if (mode === 'display_only') {
        await updateHostMode(sessionResponse.session_code, 'display_only')

        // Join session as TV player (role='tv')
        const tvJoinResponse = await joinSession(
          sessionResponse.session_code,
          'TV Display',
          'tv'
        )

        // Store TV player credentials
        setPlayer(
          tvJoinResponse.player_id,
          tvJoinResponse.name,
          sessionResponse.session_code,
          sessionResponse.id,
          gameMode as 'bingo' | 'mafia',
          true, // TV is technically the "host" device
          'tv' // role
        )

        reportSessionToPairedPhone(sessionResponse.session_code)

        // Navigate to TV lobby
        navigate(`/session/${sessionResponse.session_code}/lobby?mode=tv`)
        return
      }

      // Standard flow for "player"
      // Join session as host (this adds host to players list)
      const joinResponse = await joinSession(
        sessionResponse.session_code,
        finalHostName
      )

      // Store player data as host
      setPlayer(
        joinResponse.player_id, // Real player ID from join response
        joinResponse.name,
        sessionResponse.session_code, // ABC-123-XYZ for URL routing
        sessionResponse.id, // UUID for socket auth
        gameMode as 'bingo' | 'mafia',
        true, // isHost = true (joinResponse.is_host should also be true)
        joinResponse.role
      )

      reportSessionToPairedPhone(sessionResponse.session_code)

      // Navigate to lobby
      navigate(`/session/${sessionResponse.session_code}/lobby`)

    } catch (err) {
      const apiError = err as ApiError
      // Creating a lobby is free — coins are only charged when the game starts,
      // so the paywall lives on the start button, not here.
      setError(apiError.message ?? t('host.createGameError'))
    } finally {
      setIsLoading(false)
    }
  }

  // TV logged in by QR code: skip the form and go straight to the TV lobby;
  // the approving phone is sent to /join/{code} to set its nickname.
  const autoStartedRef = useRef(false)
  const createAndEnterLobbyRef = useRef(createAndEnterLobby)
  useEffect(() => {
    createAndEnterLobbyRef.current = createAndEnterLobby
  })
  useEffect(() => {
    if (autoStartedRef.current || !gameMode || !hasStoredPairing()) return
    autoStartedRef.current = true
    void createAndEnterLobbyRef.current('display_only')
  }, [gameMode])

  return (
    <>
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-4 py-8 md:px-6 md:py-12">
        {/* Header */}
        <header className="mb-8">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 w-fit mb-6">
            <BrandLogo className="h-8 md:h-9" />
          </Link>
          <h1 className="text-xl font-display font-semibold text-foreground">{t('host.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1 uppercase tracking-wider">
            {t('common.mode')} {gameMode !== undefined ? gameMode.toUpperCase() : t('common.unknown')}
          </p>
        </header>

        {/* Existing session banner */}
        {hasExistingSession && (
          <Card className="p-4 border-warning bg-warning/10 mb-8">
            <p className="text-warning text-center mb-3 text-sm uppercase tracking-widest font-bold">
              {t('host.activeSessionDetected')}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/session/${sessionCode}/lobby`)}
              className="w-full border-warning text-warning hover:bg-warning/20 min-h-[44px]"
            >
              {t('host.returnToActiveGame')}
            </Button>
          </Card>
        )}

        {/* Demo mode banner — visible only to free-tier authenticated hosts */}
        {showDemoBanner && (
          <Card className="p-4 border-yellow-500 bg-yellow-500/10 mb-8">
            <p className="text-yellow-600 dark:text-yellow-400 text-sm font-semibold text-center uppercase tracking-widest">
              {t('host.demoBanner')}
            </p>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
          {/* Host Mode Selection */}
          <div>
            <Label className="block text-sm font-semibold uppercase tracking-[0.2em] text-foreground mb-4">
              {t('host.modeSelectionLabel')}
            </Label>

            <div className="space-y-3">
              {/* Option: Player */}
              <Card
                role="button"
                tabIndex={isLoading ? -1 : 0}
                onClick={() => !isLoading && setHostMode('player')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (!isLoading) setHostMode('player')
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    if (!isLoading) setHostMode('display_only')
                  }
                }}
                className={`
                  p-5 md:p-4 cursor-pointer text-left transition-all
                  flex items-start gap-4
                  focus:outline-none focus:ring-2 focus:ring-ring
                  ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}
                  ${hostMode === 'player'
                    ? 'ring-2 ring-primary border-primary'
                    : 'hover:border-muted-foreground'
                  }
                `}
              >
                <PixelIcon className="text-3xl md:text-2xl mt-1 flex-shrink-0">👾</PixelIcon>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-foreground uppercase tracking-wider text-base md:text-sm">
                    {t('host.playOnPhone')}
                  </div>
                  <div className="text-sm md:text-xs text-muted-foreground mt-2 leading-relaxed">
                    {t('host.playOnPhoneDesc')}
                  </div>
                  {suggestedMode === 'player' && (
                    <div className="mt-3 text-xs md:text-[10px] text-primary uppercase tracking-widest font-bold">
                      {t('host.systemSuggestion')}
                    </div>
                  )}
                </div>
              </Card>

              {/* Option: Display only */}
              <Card
                role="button"
                tabIndex={isLoading ? -1 : 0}
                onClick={() => !isLoading && setHostMode('display_only')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (!isLoading) setHostMode('display_only')
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    if (!isLoading) setHostMode('player')
                  }
                }}
                className={`
                  p-5 md:p-4 cursor-pointer text-left transition-all
                  flex items-start gap-4
                  focus:outline-none focus:ring-2 focus:ring-ring
                  ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}
                  ${hostMode === 'display_only'
                    ? 'ring-2 ring-primary border-primary'
                    : 'hover:border-muted-foreground'
                  }
                `}
              >
                <span className="text-3xl md:text-2xl mt-1 flex-shrink-0">📺</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-foreground uppercase tracking-wider text-base md:text-sm">
                    {t('host.displayOnly')}
                  </div>
                  <div className="text-sm md:text-xs text-muted-foreground mt-2 leading-relaxed">
                    {t('host.displayOnlyDesc')}
                  </div>
                  {suggestedMode === 'display_only' && (
                    <div className="mt-3 text-xs md:text-[10px] text-primary uppercase tracking-widest font-bold">
                      {t('host.systemSuggestion')}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>

          {/* Host name input */}
          <div>
            <Label htmlFor="host-name" className="block text-sm font-semibold uppercase tracking-[0.2em] text-foreground mb-3">
              {t('host.hostNameLabel')}
            </Label>
            <Input
              id="host-name"
              type="text"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              disabled={isLoading}
              placeholder={t('host.hostNamePlaceholder')}
              maxLength={30}
              autoComplete="off"
              className="h-12 text-base"
            />
            <p className="mt-2 text-xs text-muted-foreground uppercase tracking-widest">
              {t('host.hostNameDefault')}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <Card className="p-4 border-destructive bg-destructive/10">
              <p className="text-destructive text-center text-sm uppercase tracking-widest font-bold">
                ❌ {error}
              </p>
            </Card>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 md:gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/')}
              disabled={isLoading}
              className="flex-1 min-h-[44px]"
              tabIndex={isLoading ? -1 : 0}
            >
              {t('join.back')}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 min-h-[44px] relative"
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">{t('host.createGame')}</span>
                  <span className="absolute inset-0 flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </span>
                </>
              ) : (
                `▶ ${t('host.createGame')}`
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>

    </>
  )
}
